import PropTypes from 'prop-types';
import React from 'react';
import fr from 'facet-rest';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import history from 'State/history';
import { ClientExperienceSavesHouseholdInfoOverride } from 'Overrides/ClientExperienceSavesHouseholdInfo';

const SaveStateContext = React.createContext(undefined);
const SaveDispatchContext = React.createContext(undefined);

const defaultState = {
  error: null,
  loading: null,
  refreshLink: null,
  escalatedHouseholdCollection: null,
  selectedEscalatedHousehold: null,
};

const STATE_LOADING = 'loading';
const STATE_SET_ESCALATED_HOUSEHOLD_COLLECTION = 'setEscalatedHousehold';
const STATE_SET_SELECTED_ESCALATED_HOUSEHOLD = 'setSelectedEscalatedHousehold';
const STATE_SET_REFRESH_LINK = 'setRefreshLink';

function init(state) {
  return {
    ...defaultState,
    ...state,
  };
}

const setCollection = (dispatch, collection) => {
  const currentSearchParams = new URLSearchParams(history.location.search);
  currentSearchParams.set('link', collection.link('self').url());
  history.push({
    pathname: history.location.pathname,
    search: currentSearchParams.toString(),
  });
  dispatch({ type: STATE_SET_ESCALATED_HOUSEHOLD_COLLECTION, collection });
};

const setSelectedHousehold = (dispatch, selectedHousehold) => {
  const currentSearchParams = new URLSearchParams(history.location.search);
  currentSearchParams.set('selected', selectedHousehold.link('self').url());
  currentSearchParams.set('householdId', selectedHousehold.id());
  history.push({
    pathname: history.location.pathname,
    search: currentSearchParams.toString(),
  });
  dispatch({ type: STATE_SET_SELECTED_ESCALATED_HOUSEHOLD, selectedHousehold });
};

function reducer(state, action) {
  switch (action.type) {
    case STATE_LOADING: {
      return {
        ...state,
        loading: action.loading,
      };
    }

    //  SET
    case STATE_SET_ESCALATED_HOUSEHOLD_COLLECTION: {
      return {
        ...state,
        escalatedHouseholdCollection: action.collection,
        loading: false,
      };
    }
    case STATE_SET_SELECTED_ESCALATED_HOUSEHOLD: {
      return {
        ...state,
        selectedEscalatedHousehold: action.selectedHousehold,
        loading: false,
      };
    }
    case STATE_SET_REFRESH_LINK: {
      return {
        ...state,
        refreshLink: action.link,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function SaveProvider({ children, initialState, link: linkProp }) {
  const [state, dispatch] = React.useReducer(reducer, initialState, init);

  const searchParams = new URLSearchParams(history.location.search);

  const collectionLink = searchParams.has('link') ? new fr.Link({ data: { name: 'self', url: searchParams.get('link') } }) : linkProp;
  const selectedLink = searchParams.has('selected') ? new fr.Link({ data: { name: 'self', url: searchParams.get('selected') } }) : null;

  React.useEffect(() => {
    async function fetchAndSetCollection() {
      const collection = await collectionLink.get();
      setCollection(dispatch, collection);
    }
    if (collectionLink?.url() !== state.collection?.link('self').url()) {
      fetchAndSetCollection().catch((e) => {
        displayErrorMessage(e, 'Failed to fetch escalated households');
      });
    }
  }, [collectionLink?.url()]);

  React.useEffect(() => {
    async function fetchAndSetEscalatedHousehold() {
      if (selectedLink !== null) {
        const [escalatedHousehold] = await selectedLink.get({ metaOverrideSet: ClientExperienceSavesHouseholdInfoOverride });
        setSelectedHousehold(dispatch, escalatedHousehold);
      }
    }
    if (selectedLink?.url() !== state.selectedEscalatedHousehold?.link('self').url()) {
      fetchAndSetEscalatedHousehold().catch((e) => {
        displayErrorMessage(e, 'Failed to fetch selected escalated household');
      });
    }
  }, [selectedLink?.url()]);

  return (
    <SaveStateContext.Provider value={state}>
      <SaveDispatchContext.Provider value={dispatch}>
        {children}
      </SaveDispatchContext.Provider>
    </SaveStateContext.Provider>
  );
}

function useSaveStateContext() {
  const context = React.useContext(SaveStateContext);

  if (context === undefined) {
    throw new Error('useSaveStateContext must be used within a SaveProvider');
  }

  return context;
}

function useSaveDispatchContext() {
  const context = React.useContext(SaveDispatchContext);

  if (context === undefined) {
    throw new Error('useSaveDispatchContext must be used within a SaveProvider');
  }

  return context;
}

export function setRefreshLink(link, dispatch) {
  dispatch({ type: STATE_SET_REFRESH_LINK, link });
}

export async function refreshDetails(url, dispatch) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const [resource] = fr.collection(await fr.http.Get(url));
    dispatch({ type: STATE_SET_SELECTED_ESCALATED_HOUSEHOLD, resource });
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}

export async function refreshEscalatedHouseholds(url, dispatch) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const collection = fr.collection(await fr.http.Get(url));
    dispatch({ type: STATE_SET_ESCALATED_HOUSEHOLD_COLLECTION, collection });
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}

SaveProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  initialState: PropTypes.object.isRequired,
  link: PropTypes.object.isRequired,
};

export {
  SaveProvider,
  useSaveStateContext,
  useSaveDispatchContext,
};
